/**
 * The door of Acceso and the hand-over into the Panel.
 *
 * The door: on a wide or landscape screen a split, the owner's photograph of the pass on the left and the column on
 * the paper with the «Orbes» on the right, with no card around it; on a phone the photograph is a band at the top that
 * leaves upwards when the keyboard opens, so «Entrar» is always above the keys.
 *
 * The stage (`AccesoStage`, mounted by the runtime boundary) keeps one door for the whole access family, so the
 * photograph and the orbs stay while its content changes (the login, «Elegir dónde entrar», «Crea tu PIN»…), and plays
 * the hand-over: after «Entrar» the tick draws, the Panel is drawn hidden under the door, then the door recedes, the
 * Exylon mark travels to the rail's tile and the screen rises behind it. Leaving the Panel («No soy …», «Cerrar
 * sesión») plays it backwards. Under reduced motion, or where nothing can be measured, every change lands at once.
 */

import { AnimatePresence, LayoutGroup } from './living/presence';
import { m } from './living/elements';
import {
  createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import type { ReactNode, Ref, RefObject } from 'react';

import { Brand, ExylonMark, Hero, Row, useDoorStill, type DoorPerson } from './acceso-parts';
import { LivingBackground } from './living/living-background';
import { MotionProvider, SPRING } from './living/motion-provider';
import { DOOR_THEME, usePageTheme } from './theme/page-theme';

// The door's night colours, which the entry stylesheet has no room for (`theme/night-lazy.scss`): their own file, which
// the build links from `index.html` as a stylesheet (`scripts/night-sheet.mjs`), so the first paint waits for it and the
// service worker keeps it offline. This import is what makes the build write the file; in the built page it finds the
// link and fetches nothing. Should it still fail, the door keeps its day colours at night and nothing else is touched:
// the failure is marked handled, so no screen's load boundary reads it as its own.
const NIGHT_SHEET = /night-lazy/;
window.addEventListener('vite:preloadError', (event) => {
  if (NIGHT_SHEET.test(String(event.payload?.message))) event.preventDefault();
});
import('./theme/night-lazy.scss').catch(() => undefined);

/* ── The Madrid hour ────────────────────────────────────────────────────────────────────────────────────────── */

const MADRID_HOUR = new Intl.DateTimeFormat('es-ES', { timeZone: 'Europe/Madrid', hour: 'numeric', hourCycle: 'h23' });

/** @internal Exported for its spec. The hour on the restaurant's wall (Spain only: Europe/Madrid), whatever the device's zone. */
export const madridHour = (now: Date = new Date()): number => Number(MADRID_HOUR.format(now));

export type Greeting = 'Buenos días' | 'Buenas tardes' | 'Buenas noches';

/** @internal Exported for its spec. Spanish use: «días» until lunch, «tardes» until dinner, «noches» after. */
export const greetingFor = (hour: number): Greeting =>
  hour >= 6 && hour < 14 ? 'Buenos días' : hour >= 14 && hour < 21 ? 'Buenas tardes' : 'Buenas noches';

/** The greeting of the hour, kept current while the door stays open across a change of part of the day. */
export function useGreeting(): Greeting {
  const [greeting, setGreeting] = useState(() => greetingFor(madridHour()));
  useEffect(() => {
    const timer = window.setInterval(() => setGreeting(greetingFor(madridHour())), 60_000);
    return () => window.clearInterval(timer);
  }, []);
  return greeting;
}

/* ── The door ───────────────────────────────────────────────────────────────────────────────────────────────── */

export type AccesoView = 'restoring' | 'login' | 'picker' | 'rotation' | 'claim' | 'unassigned' | 'cannot-open';

interface DoorMeta {
  readonly context: string;
  readonly person: DoorPerson | undefined;
  readonly screenLabel: string | undefined;
}

interface DoorFrameApi {
  /** The content says what the brand row shows. */
  readonly register: (meta: DoorMeta) => void;
  /** A form is waiting on the Service: the mark breathes. */
  readonly setChecking: (checking: boolean) => void;
  /** The mark the hand-over measures and sends travelling (the brand's, or the big one of «Confirmando tu sesión»). */
  readonly markRef: Ref<HTMLSpanElement>;
  readonly hideMark: boolean;
}

const DoorFrameContext = createContext<DoorFrameApi | null>(null);

/** A form that waits on the Service makes the door's mark breathe («Comprobando…», «Entrando…»). */
export function useDoorChecking(checking: boolean): void {
  const frame = useContext(DoorFrameContext);
  const setChecking = frame?.setChecking;
  useEffect(() => {
    if (setChecking === undefined) return undefined;
    setChecking(checking);
    return () => setChecking(false);
  }, [checking, setChecking]);
}

/** The mark of the door, for a content that draws its own (`Restoring`). */
export function useDoorMark(): { readonly markRef: Ref<HTMLSpanElement> | undefined; readonly hidden: boolean } {
  const frame = useContext(DoorFrameContext);
  return { markRef: frame?.markRef, hidden: frame?.hideMark ?? false };
}

const ROW_EXIT = { opacity: 0, transform: 'translateY(-10px)', transition: { duration: 0.18 } } as const;

/**
 * Hooks the content of a door into its frame. Inside the stage, the frame is the stage's (one for the whole access
 * family) and the content only registers what its brand row says; on its own (a spec, the proof gallery), it draws a
 * complete door.
 */
export function AccesoDoor({ view, context = 'Exylon', person, screenLabel, children }: {
  readonly view: AccesoView;
  readonly context?: string;
  readonly person?: DoorPerson | undefined;
  readonly screenLabel?: string;
  readonly children: ReactNode;
}) {
  const frame = useContext(DoorFrameContext);
  const register = frame?.register;
  const personName = person?.name;
  const personAvatar = person?.avatarUrl;
  useLayoutEffect(() => {
    register?.({ context, person: personName === undefined ? undefined : { name: personName, avatarUrl: personAvatar }, screenLabel });
  }, [context, personAvatar, personName, register, screenLabel]);
  if (frame !== null) return <>{children}</>;
  return <StandaloneDoor context={context} person={person} screenLabel={screenLabel} view={view}>{children}</StandaloneDoor>;
}

function StandaloneDoor({ view, context, person, screenLabel, children }: {
  readonly view: AccesoView;
  readonly context: string;
  readonly person: DoorPerson | undefined;
  readonly screenLabel: string | undefined;
  readonly children: ReactNode;
}) {
  const meta = useMemo(() => ({ context, person, screenLabel }), [context, person, screenLabel]);
  return (
    <MotionProvider features="layout">
      <DoorFrame meta={meta} view={view}>{children}</DoorFrame>
    </MotionProvider>
  );
}

/**
 * The frame: the paper, the orbs, the photograph and the column with the brand row. `leaving`: the hand-over has
 * started, the door recedes. `arriving`: the door comes back over the leaving Panel.
 */
function DoorFrame({ view, meta, register, leaving = false, arriving = false, hideMark = false, brandRises = true, markRef: givenMarkRef,
  children }: {
  readonly view: AccesoView;
  readonly meta: DoorMeta;
  readonly register?: (meta: DoorMeta) => void;
  readonly leaving?: boolean;
  readonly arriving?: boolean;
  readonly hideMark?: boolean;
  readonly brandRises?: boolean;
  readonly markRef?: Ref<HTMLSpanElement>;
  readonly children: ReactNode;
}) {
  const ownMarkRef = useRef<HTMLSpanElement | null>(null);
  const markRef = givenMarkRef ?? ownMarkRef;
  const [checking, setChecking] = useState(false);
  const solo = view === 'restoring';
  const main = useRef<HTMLElement>(null);
  const keyboard = usePhoneKeyboard(main);
  const still = useDoorStill();
  const api = useMemo<DoorFrameApi>(() => ({ register: register ?? (() => undefined), setChecking, markRef, hideMark }),
    [hideMark, markRef, register]);
  return (
    <DoorFrameContext.Provider value={api}>
      <m.main animate={leaving ? { opacity: 0, transform: 'scale(0.965)' } : { opacity: 1, transform: 'scale(1)' }} className="acc"
        data-keyboard={keyboard || undefined} data-screen-label={meta.screenLabel} data-view={view}
        initial={arriving && !still ? { opacity: 0 } : false} ref={main} transition={{ ...SPRING.soft, visualDuration: leaving ? 0.5 : 0.45 }}>
        <LivingBackground />
        <div className="acc__door" data-solo={solo || undefined}>
          {!solo && <div className="acc__hero"><Hero /></div>}
          <div className="acc__col">
            <div className="acc__inner">
              <LayoutGroup>
                {!solo && <Row index={0} rises={brandRises}>
                  <Brand breathing={checking} context={meta.context} hideMark={hideMark} markRef={markRef} person={meta.person} />
                </Row>}
                {children}
              </LayoutGroup>
            </div>
          </div>
        </div>
      </m.main>
    </DoorFrameContext.Provider>
  );
}

/**
 * The phone's keyboard. With it open the photograph leaves upwards and the column follows, so «Entrar» stays above the
 * keys. Android resizes the page (`interactive-widget=resizes-content`, index.html); iOS shrinks the visual viewport:
 * both are read from `visualViewport`, only while the focus is inside the door.
 */
function usePhoneKeyboard(main: RefObject<HTMLElement | null>): boolean {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const viewport = window.visualViewport;
    if (viewport === null || viewport === undefined) return undefined;
    let tallest = Math.max(viewport.height, window.innerHeight);
    const update = () => {
      const height = viewport.height;
      tallest = Math.max(tallest, height, document.documentElement.clientHeight);
      const active = document.activeElement;
      // A field of the door has the focus, or nothing has it (a pressed «Entrar» goes busy while the keys are still up).
      const typing = active === null || active === document.body || main.current?.contains(active) === true;
      setOpen(typing && window.innerWidth <= 760 && height < tallest - 120);
    };
    viewport.addEventListener('resize', update);
    document.addEventListener('focusin', update);
    document.addEventListener('focusout', update);
    return () => {
      viewport.removeEventListener('resize', update);
      document.removeEventListener('focusin', update);
      document.removeEventListener('focusout', update);
    };
  }, [main]);
  return open;
}

/* ── The stage: one door for the family, and the hand-over ──────────────────────────────────────────────────── */

export interface Box { readonly left: number; readonly top: number; readonly width: number; readonly height: number }

/** How far the Panel rises as it arrives behind the receding door. */
const SHELL_RISE = 18;
/** The tick draws before the door hands over (it is drawn in 0.42 s). */
const TICK_HOLD_MS = 560;

const boxOf = (rect: DOMRect): Box => ({ left: rect.left, top: rect.top, width: rect.width, height: rect.height });
const measurable = (element: Element | null | undefined): Box | null => {
  const rect = element?.getBoundingClientRect();
  return rect !== undefined && rect.width > 0 && rect.height > 0 ? boxOf(rect) : null;
};
/** The rail's tile, when there is a rail (on a phone the shell has none). */
const railBox = (root: Element | null): Box | null => {
  const box = measurable(root?.querySelector('.app-navigation__brand'));
  return box !== null && box.width >= 16 && box.width <= 90 ? box : null;
};

type Handover = 'none' | 'tick' | 'mounting' | 'in';
interface Flight { readonly from: Box; readonly to: Box | null; readonly direction: 'in' | 'out' | 'wake' }
interface Shown { readonly view: AccesoView; readonly node: ReactNode }

/**
 * One door for the access family and the Panel behind it. `view` and `door` name the door the runtime shows now
 * (`null` once the Panel opens); `children` is the Panel.
 */
export function AccesoStage({ view, door, children }: {
  readonly view: AccesoView | null;
  readonly door: ReactNode;
  readonly children?: ReactNode;
}) {
  // A door has no screen: it applies its own page theme (day until the doors' night is drawn; then the device's choice).
  usePageTheme(DOOR_THEME);
  const still = useDoorStill();
  const panelRef = useRef<HTMLDivElement>(null);
  const mark = useRef<HTMLSpanElement | null>(null);
  // Two marks can be in the door for a moment (the one of «Confirmando tu sesión» leaving, the brand's arriving): the
  // one that stays connected is the door's.
  const markRef = useCallback((node: HTMLSpanElement | null) => {
    if (node !== null) mark.current = node;
    else if (mark.current !== null && !mark.current.isConnected) mark.current = null;
  }, []);
  const [meta, setMeta] = useState<DoorMeta>({ context: 'Exylon', person: undefined, screenLabel: undefined });
  const register = useCallback((next: DoorMeta) => setMeta((current) => (current.context === next.context
    && current.screenLabel === next.screenLabel && current.person?.name === next.person?.name
    && current.person?.avatarUrl === next.person?.avatarUrl ? current : next)), []);

  // The door drawn now: the runtime's, or the last one while it hands over (its form keeps its tick).
  const [shown, setShown] = useState<Shown | null>(view === null ? null : { view, node: door });
  const [handover, setHandover] = useState<Handover>('none');
  const [flight, setFlight] = useState<Flight | null>(null);
  const flightRef = useRef<Flight | null>(null);
  flightRef.current = flight;
  const [arrived, setArrived] = useState(false);
  const [arriving, setArriving] = useState(false);
  const pendingWake = useRef<Box | null>(null);
  const pendingOut = useRef<{ readonly rail: Box | null; readonly snapshot: HTMLElement | null } | null>(null);
  const [leavingPanel, setLeavingPanel] = useState<HTMLElement | null>(null);
  const previousView = useRef<AccesoView | null>(view);
  const panelOpen = view === null;

  // Before this render commits, the Panel's DOM is still there: a Panel that gives the door back is measured now.
  if (previousView.current === null && view !== null && pendingOut.current === null && !still) {
    const root = panelRef.current;
    const picture = root?.firstElementChild;
    pendingOut.current = {
      rail: railBox(root),
      snapshot: picture instanceof HTMLElement && measurable(picture) !== null ? picture.cloneNode(true) as HTMLElement : null,
    };
  }
  // «Confirmando tu sesión» that found no session: its mark becomes the login's.
  if (previousView.current === 'restoring' && view === 'login' && pendingWake.current === null && !still) {
    pendingWake.current = measurable(mark.current);
  }

  useLayoutEffect(() => {
    const before = previousView.current;
    previousView.current = view;
    if (view !== null) {
      if (pendingOut.current !== null) setArriving(true);
      // A door of the family: draw it now, except the login or the PIN that has just been answered, which keeps its
      // tick on screen for a moment before the next door («Elegir dónde entrar») takes its place.
      const ticking = (before === 'login' || before === 'rotation') && view !== before && !still && measurable(mark.current) !== null;
      if (!ticking) { setShown({ view, node: door }); return undefined; }
      const timer = window.setTimeout(() => setShown({ view, node: door }), TICK_HOLD_MS);
      return () => window.clearTimeout(timer);
    }
    // The Panel opens.
    if (before === null) return undefined;
    const from = measurable(mark.current);
    if (still || from === null) { setShown(null); setHandover('none'); return undefined; }
    const hold = before === 'login' || before === 'rotation' ? TICK_HOLD_MS : 0;
    setHandover('tick');
    const timer = window.setTimeout(() => setHandover('mounting'), hold);
    return () => window.clearTimeout(timer);
    // `door` is a new element on every render of the boundary; only a change of view starts something here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, still]);

  // The Panel has been drawn, hidden under the still door: two frames after its first paint, the journey starts.
  useEffect(() => {
    if (handover !== 'mounting') return undefined;
    let second = 0;
    const first = window.requestAnimationFrame(() => {
      second = window.requestAnimationFrame(() => {
        const from = measurable(mark.current);
        const rail = railBox(panelRef.current);
        if (from === null) { setShown(null); setHandover('none'); return; }
        setHandover('in');
        setFlight({ from, to: rail === null ? null : { ...rail, top: rail.top - SHELL_RISE }, direction: 'in' });
      });
    });
    return () => { window.cancelAnimationFrame(first); window.cancelAnimationFrame(second); };
  }, [handover]);

  // A door arrives over a Panel that has gone, or over «Confirmando tu sesión»: the mark comes back to its brand row.
  useLayoutEffect(() => {
    if (shown === null) { setArriving(false); return undefined; }
    const out = pendingOut.current;
    const wake = pendingWake.current;
    if (out === null && wake === null) return undefined;
    pendingOut.current = null;
    pendingWake.current = null;
    if (out?.snapshot) setLeavingPanel(out.snapshot);
    const from = out === null ? wake : out.rail;
    if (from === null) return undefined;
    const frame = window.requestAnimationFrame(() => {
      const to = measurable(mark.current);
      if (to !== null) setFlight({ from, to, direction: out === null ? 'wake' : 'out' });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [shown]);

  const landed = useCallback(() => {
    if (flightRef.current?.direction === 'in') { setShown(null); setHandover('none'); }
    setFlight(null);
    setArrived(false);
  }, []);

  // The same door re-renders with the runtime's latest props (a notice, a failure); a door being held keeps its own.
  const drawn = shown !== null && shown.view === view ? { view, node: door } : shown;
  const travelling = flight !== null && !arrived;
  const panelHidden = handover === 'tick' || (drawn !== null && panelOpen && handover === 'none');
  return (
    <>
      {panelOpen && !panelHidden && (
        <div className="acc-panel" data-flight={(flight?.direction === 'in' && travelling) || undefined}
          data-handover={handover === 'none' ? undefined : handover} ref={panelRef}>
          {children}
        </div>
      )}
      {leavingPanel !== null && <LeavingPanel onDone={() => setLeavingPanel(null)} snapshot={leavingPanel} />}
      {drawn !== null && (
        <MotionProvider features="layout">
          <DoorFrame arriving={arriving} brandRises={pendingOut.current === null && pendingWake.current === null && flight === null}
            hideMark={travelling} leaving={handover === 'in'} markRef={markRef} meta={meta} register={register} view={drawn.view}>
            <AnimatePresence mode="wait">
              <m.div animate={{ opacity: 1, transform: 'translateY(0px)' }} className="acc__content" {...(still ? {} : { exit: ROW_EXIT })} initial={false} key={drawn.view}>
                {drawn.node}
              </m.div>
            </AnimatePresence>
          </DoorFrame>
        </MotionProvider>
      )}
      {flight !== null && <MotionProvider><FlyingMark flight={flight} onArrive={() => setArrived(true)} onDone={landed} /></MotionProvider>}
    </>
  );
}

/** The Panel that has just given the door back: a still copy of its last picture that recedes and goes. */
function LeavingPanel({ snapshot, onDone }: { readonly snapshot: HTMLElement; readonly onDone: () => void }) {
  const host = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const element = host.current;
    if (element === null) return undefined;
    snapshot.setAttribute('inert', '');
    snapshot.setAttribute('aria-hidden', 'true');
    element.replaceChildren(snapshot);
    const timer = window.setTimeout(onDone, 560);
    return () => { window.clearTimeout(timer); element.replaceChildren(); };
    // Played once, for this copy.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot]);
  return <m.div animate={{ opacity: 0, transform: 'translateY(14px)' }} aria-hidden="true" className="acc-panel acc-panel--leaving"
    initial={{ opacity: 1, transform: 'translateY(0px)' }} ref={host} transition={SPRING.soft} />;
}

/**
 * The mark leaves the door and lands on the rail's tile (or the other way). One transform per axis: the outer copy
 * moves across on the soft spring, the inner one down and to size on a slightly slower one, so the path bows. Where the
 * shell has no rail (a phone), the mark rises to the top corner and fades as the screen arrives.
 */
function FlyingMark({ flight, onArrive, onDone }: {
  readonly flight: Flight;
  readonly onArrive: () => void;
  readonly onDone: () => void;
}) {
  const { from, to } = flight;
  const target = to ?? { left: 18, top: 14, width: 30, height: 30 };
  const fades = to === null;
  const [arrived, setArrived] = useState(false);
  const dx = target.left - from.left;
  const dy = target.top - from.top;
  const scale = target.width / from.width;
  const arrive = useRef(onArrive);
  arrive.current = onArrive;
  const done = useRef(onDone);
  done.current = onDone;
  useEffect(() => {
    if (!arrived) return undefined;
    arrive.current();
    const timer = window.setTimeout(() => done.current(), fades ? 40 : 170);
    return () => window.clearTimeout(timer);
  }, [arrived, fades]);
  return (
    <m.div animate={{ transform: `translateX(${dx}px)` }} aria-hidden="true" className="acc-flight" initial={{ transform: 'translateX(0px)' }}
      style={{ left: from.left, top: from.top, width: from.width, height: from.height }} transition={{ ...SPRING.soft, visualDuration: 0.62 }}>
      <m.div animate={{ transform: `translateY(${dy}px) scale(${scale})`, opacity: arrived || fades ? 0 : 1 }} className="acc-flight__in"
        initial={{ transform: 'translateY(0px) scale(1)', opacity: 1 }} onAnimationComplete={() => setArrived(true)}
        transition={{ transform: { ...SPRING.soft, visualDuration: 0.74 }, opacity: fades ? { duration: 0.7, ease: [0.6, 0, 0.9, 0.4] } : { duration: 0.14 } }}>
        <ExylonMark size={from.width} />
      </m.div>
    </m.div>
  );
}
